import type { TeenGigRequestListItemResponse } from "../../../types/gigTypes";
import type { DataTableColumn } from "../../ui/DataTable/types";
import ResponsiveDataList from "../../ui/DataTable/ResponsiveDataList";
import Button from "../../ui/Button/Button";
import InfoTooltip from "../../ui/InfoTooltip/InfoTooltip";
import {
  requestedGigStatusLabel,
  requestedGigStatusPillClassName,
  toReadableDate,
} from "../dashboard/shared/dashboardDisplay";
import { WITHDRAWABLE_STATUSES } from "./constants";
import {
  formatGigPayment,
  getRequestStageClassName,
  getRequestStageLabel,
  isAwaitingTeenScheduleConfirmation,
} from "./utils";

type TeenRequestedGigsTableProps = {
  gigs: TeenGigRequestListItemResponse[];
  onOpenDetails: (gig: TeenGigRequestListItemResponse) => void;
  onOpenConversation: (gig: TeenGigRequestListItemResponse) => void;
  onOpenWithdraw: (gig: TeenGigRequestListItemResponse) => void;
  onApplyAgain: (gig: TeenGigRequestListItemResponse) => void;
  onOpenPayoutSetup: (gig: TeenGigRequestListItemResponse) => void;
};

const requestTableColumns: DataTableColumn[] = [
  { key: "gig", label: "Gig", className: "px-5 py-4 text-center text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted" },
  { key: "status", label: "Status", className: "px-5 py-4 text-center text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted" },
  { key: "payment", label: "Compensation", className: "px-5 py-4 text-center text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted" },
  { key: "requested", label: "Applied", className: "px-5 py-4 text-center text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted" },
  { key: "actions", label: "Actions", className: "px-5 py-4 text-center text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted" },
];

const posterApprovalHelpText =
  "Your application is waiting for the gig poster to review. They can accept, decline, or suggest a different time. If they accept you for a paid gig, funding is the next step.";

const payoutSetupButtonClassName =
  "!border-emerald-700 !bg-emerald-600 !text-white hover:!bg-emerald-700 focus:!ring-emerald-500 focus-visible:!outline-emerald-600";

const awaitingScheduleConfirmationHelpText =
  "The poster suggested a time window. Confirm it or suggest a different start time in Message / Schedule so they can continue reviewing your application.";

const statusHelpText: Record<TeenGigRequestListItemResponse["status"], string> = {
  GUARDIAN_PENDING:
    "Your connected parent or guardian needs to approve this specific gig application before the gig poster can review it.",
  POSTER_PENDING: posterApprovalHelpText,
  POSTER_SELECTED_PENDING_CREW:
    "The poster selected you for a multi-teen gig. The gig is waiting for enough selected teens before payment is confirmed.",
  PAYOUT_SETUP_PENDING:
    "The poster has selected you, but this gig cannot be funded until payout setup is ready. If your payouts are guardian-managed, open Payout Account settings and send the setup link to your guardian if you have not already.",
  FUNDING_PENDING:
    "Your payout setup is ready. The poster still needs to pay to fully confirm the gig.",
  FILLED_BY_OTHER_TEEN:
    "The poster chose another applicant, so this application is no longer active.",
  GUARDIAN_REJECTED:
    "A parent or guardian declined this application.",
  POSTER_REJECTED:
    "The gig poster declined this application.",
  EXPIRED:
    "The gig's available time passed before this application was accepted.",
  WITHDRAWN:
    "You withdrew this application. If the gig is still open, you may be able to apply again.",
  REMOVED_BY_POSTER:
    "The poster removed you from this scheduled gig.",
  REMOVED_BY_TEEN:
    "You left this scheduled gig.",
  POSTER_ACCEPTED:
    "The poster accepted this application. You can find the gig in Scheduled Gigs.",
  CANCELLED:
    "This gig was cancelled after acceptance.",
};

const getRequestedGigStatusDisplay = (gig: TeenGigRequestListItemResponse) => {
  if (isAwaitingTeenScheduleConfirmation(gig)) {
    return {
      label: "Confirm Time Window",
      pillClassName: "bg-amber-100 text-amber-900 border-amber-200",
      helpText: awaitingScheduleConfirmationHelpText,
    };
  }

  return {
    label: requestedGigStatusLabel[gig.status],
    pillClassName: requestedGigStatusPillClassName[gig.status],
    helpText: statusHelpText[gig.status],
  };
};

const RequestedGigStatusBadge = ({ gig }: { gig: TeenGigRequestListItemResponse }) => {
  const statusDisplay = getRequestedGigStatusDisplay(gig);

  return (
    <div className="inline-flex items-center justify-center gap-1">
      <span
        className={`inline-flex max-w-[260px] whitespace-normal rounded-full border px-3 py-1 text-xs font-semibold leading-5 ${statusDisplay.pillClassName}`}
      >
        {statusDisplay.label}
      </span>
      <InfoTooltip
        message={statusDisplay.helpText}
        label={`${statusDisplay.label} info`}
        placement="top-right"
        className="shrink-0"
      />
    </div>
  );
};

const TeenRequestedGigsTable = ({
  gigs,
  onOpenDetails,
  onOpenConversation,
  onOpenWithdraw,
  onApplyAgain,
  onOpenPayoutSetup,
}: TeenRequestedGigsTableProps) => {
  const hasConversationAccess = (gig: TeenGigRequestListItemResponse) =>
    gig.status === "POSTER_PENDING"
    || gig.status === "GUARDIAN_PENDING"
    || gig.status === "PAYOUT_SETUP_PENDING"
    || gig.status === "FUNDING_PENDING";
  const isWaitingOnPosterAcceptance = (gig: TeenGigRequestListItemResponse) =>
    gig.status === "POSTER_PENDING" && gig.teenConfirmedPosterTimeAt !== null;
  const isWithdrawable = (gig: TeenGigRequestListItemResponse) => WITHDRAWABLE_STATUSES.includes(gig.status);
  const canApplyAgain = (gig: TeenGigRequestListItemResponse) => gig.status === "WITHDRAWN" && gig.canRequestAgain && Boolean(gig.gigId);

  return (
    <ResponsiveDataList
      items={gigs}
      columns={requestTableColumns}
      getItemKey={(gig) => gig.requestId}
      className="mt-6"
      renderMobileCard={(gig) => {
        const isAwaitingScheduleConfirmation = isAwaitingTeenScheduleConfirmation(gig);

        return (
          <article
            id={`teen-request-${gig.requestId}`}
            className="rounded-[1.5rem] border border-myteengig-border bg-white shadow-sm"
          >
            <div className="rounded-[1.5rem] bg-[radial-gradient(circle_at_top_left,_rgba(163,230,53,0.18),_transparent_40%),linear-gradient(180deg,_rgba(255,255,255,1),_rgba(249,250,251,1))] p-5 text-left">
              <div className="flex flex-col items-start gap-3 sm:flex-row sm:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${getRequestStageClassName(gig.status)}`}>
                      {getRequestStageLabel(gig.status)}
                    </span>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">
                      {gig.gigCategory ?? "Gig"}
                    </p>
                  </div>
                  <div className="mt-3">
                    <h2 className="break-words text-xl font-semibold text-myteengig-text-primary">{gig.gigTitle ?? "Untitled Gig"}</h2>
                  </div>
                </div>
                <RequestedGigStatusBadge gig={gig} />
              </div>

              {isAwaitingScheduleConfirmation ? (
                <p className="mt-3 text-sm font-semibold text-amber-800">
                  Poster suggested a time window. Open Message / Schedule to confirm it or suggest another start time.
                </p>
              ) : null}
              {isWaitingOnPosterAcceptance(gig) ? (
                <p className="mt-3 text-sm font-medium text-emerald-700">
                  You confirmed the latest suggested time. Waiting on the poster to accept this gig.
                </p>
              ) : null}
              {gig.status === "PAYOUT_SETUP_PENDING" ? (
                <p className="mt-3 text-sm font-semibold text-emerald-700">
                  The Poster Has Selected you!
                </p>
              ) : null}
              {gig.status === "FUNDING_PENDING" ? (
                <p className="mt-3 text-sm font-medium text-emerald-700">
                  Your payout setup is ready. The poster still needs to pay to fully confirm the gig.
                </p>
              ) : null}

              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-2xl bg-white/80 px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Compensation</p>
                  <p className="mt-1 font-semibold text-myteengig-text-primary">{formatGigPayment(gig.paymentAmountCents, gig.volunteer)}</p>
                </div>
                <div className="rounded-2xl bg-white/80 px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Applied</p>
                  <p className="mt-1 font-semibold text-myteengig-text-primary">{toReadableDate(gig.requestedAt)}</p>
                </div>
              </div>

              <div className="mt-4 grid gap-3">
                {gig.status === "PAYOUT_SETUP_PENDING" ? (
                  <Button className={`w-full ${payoutSetupButtonClassName}`} onClick={() => onOpenPayoutSetup(gig)}>
                    Finish Setup to Proceed
                  </Button>
                ) : null}
                {isWithdrawable(gig) ? (
                  <Button variant="outline" className="w-full" onClick={() => onOpenWithdraw(gig)}>
                    Withdraw Request
                  </Button>
                ) : null}
                {canApplyAgain(gig) ? (
                  <Button variant="outline" className="w-full" onClick={() => onApplyAgain(gig)}>
                    Apply Again
                  </Button>
                ) : null}
                <Button variant="outline" className="w-full" onClick={() => onOpenDetails(gig)}>
                  View Details
                </Button>
                {hasConversationAccess(gig) ? (
                  <Button className="w-full" onClick={() => onOpenConversation(gig)}>
                    {isAwaitingScheduleConfirmation ? "Confirm / Schedule" : "Message / Schedule"}
                  </Button>
                ) : null}
              </div>
            </div>
          </article>
        );
      }}
      renderDesktopRows={(gig) => {
        const isAwaitingScheduleConfirmation = isAwaitingTeenScheduleConfirmation(gig);

        return (
          <tr id={`teen-request-${gig.requestId}`} key={gig.requestId} className="align-top hover:bg-myteengig-background">
            <td className="px-5 py-5 text-center">
              <p className="text-base font-semibold leading-6 text-myteengig-text-primary">{gig.gigTitle ?? "Untitled Gig"}</p>
              <p className="mt-1 text-sm text-myteengig-text-muted">{gig.gigCategory ?? "Gig"}</p>
              {isAwaitingScheduleConfirmation ? (
                <p className="mt-2 text-sm font-semibold text-amber-800">
                  Poster suggested a time window. Confirm or suggest another start time.
                </p>
              ) : null}
              {isWaitingOnPosterAcceptance(gig) ? (
                <p className="mt-2 text-sm font-medium text-emerald-700">
                  Time confirmed. Waiting on the poster to accept.
                </p>
              ) : null}
              {gig.status === "PAYOUT_SETUP_PENDING" ? (
                <p className="mt-2 text-sm font-semibold text-emerald-700">
                  The Poster Has Selected you!
                </p>
              ) : null}
              {gig.status === "FUNDING_PENDING" ? (
                <p className="mt-2 text-sm font-medium text-emerald-700">
                  Payout setup ready. Waiting on poster payment.
                </p>
              ) : null}
            </td>
            <td className="px-5 py-5">
              <div className="flex flex-col items-center gap-2">
                <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${getRequestStageClassName(gig.status)}`}>
                  {getRequestStageLabel(gig.status)}
                </span>
                <RequestedGigStatusBadge gig={gig} />
              </div>
            </td>
            <td className="px-5 py-5 text-center text-sm font-semibold leading-6 text-myteengig-text-primary">{formatGigPayment(gig.paymentAmountCents, gig.volunteer)}</td>
            <td className="px-5 py-5 text-center text-sm leading-6 text-myteengig-text-primary">
              <span>{toReadableDate(gig.requestedAt)}</span>
            </td>
            <td className="px-5 py-5">
              <div className="flex flex-col items-center gap-2">
                {gig.status === "PAYOUT_SETUP_PENDING" ? (
                  <Button size="sm" className={`w-full max-w-[180px] ${payoutSetupButtonClassName}`} onClick={() => onOpenPayoutSetup(gig)}>
                    Finish Setup to Proceed
                  </Button>
                ) : null}
                {isWithdrawable(gig) ? (
                  <Button size="sm" variant="outline" className="w-full max-w-[180px]" onClick={() => onOpenWithdraw(gig)}>
                    Withdraw Request
                  </Button>
                ) : null}
                {canApplyAgain(gig) ? (
                  <Button size="sm" variant="outline" className="w-full max-w-[180px]" onClick={() => onApplyAgain(gig)}>
                    Apply Again
                  </Button>
                ) : null}
                <Button size="sm" variant="outline" className="w-full max-w-[180px]" onClick={() => onOpenDetails(gig)}>
                  View Details
                </Button>
                {hasConversationAccess(gig) ? (
                  <Button size="sm" className="w-full max-w-[180px]" onClick={() => onOpenConversation(gig)}>
                    {isAwaitingScheduleConfirmation ? "Confirm / Schedule" : "Message / Schedule"}
                  </Button>
                ) : null}
              </div>
            </td>
          </tr>
        );
      }}
    />
  );
};

export default TeenRequestedGigsTable;
